import { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import FormikSearchableDropdown from './FormikSearchableDropdown';
import { getRegions } from '../services/project/projectService';
import { getAreas } from '../services/project/areaService';
import { createRole, updateRole } from '../services/project/roleService';
import { useParams } from 'react-router-dom';
import { useProjects } from '../context/ProjectContext';

const EditRoleModal = ({ role, show, onClose, refetch = () => {} }) => {
  const { projects } = useProjects();
  const { projectId } = useParams();
  const currentProject = projects?.find(p => String(p.id) === String(projectId));
  const [permissions, setPermissions] = useState({
    Stores: { Read: true },
    Users: { Read: true },
    'Engagements (Reports)': {
      Create: true,
      Read: true,
      Update: true,
    },
  });
  const [regionOptions, setRegionOptions] = useState([]);
  const [areaOptions, setAreaOptions] = useState({});

  const expandPermissions = (permObj = {}) =>
    Object.fromEntries(
      Object.entries(permObj).map(([section, actions]) => [
        section,
        Object.fromEntries(actions.map(a => [a, true]))
      ])
    );

  const formik = useFormik({
    initialValues: {
      roleName: '',
      regionIds: [],
      areaByRegion: {},
      access: [],
      isLeadership: false,
      approver: false,
      validator: false,
      trackAttendance: false,
    },
    validationSchema: Yup.object({
      roleName: Yup.string().required('Role name is required'),
      regionIds: Yup.array().of(Yup.number()).min(1, 'Select at least one region'),
      areaByRegion: Yup.object(),
      access: Yup.array().of(Yup.string().oneOf(['web', 'mobile'])).min(1, 'Select at least one access type'),
      isLeadership: Yup.boolean(),
    }),
    onSubmit: async ({ roleName, access, areaByRegion, isLeadership, trackAttendance }) => {
      const roleDetails = {
        name: roleName,
        regionIds: Object.entries(areaByRegion).map(([regionId, areas]) => ({
          regionId: parseInt(regionId, 10),
          areas: areas.map(a => parseInt(a, 10)),
        })),
        permissions: transformPermissions(permissions),
        projectId,
        access,
        isLeadership,
        approver: formik.values.approver,
        validator: formik.values.validator,
        trackAttendance,
      };
      try {
        let response;
        if (role) {
          response = await updateRole({ id: role.id, ...roleDetails });
        } else {
          response = await createRole({ ...roleDetails });
        }
        onClose();
        toast.success(response.message || 'Role successfully created!');
        refetch();
      } catch (error) {
        console.error('Error creating role:', error);
        toast.error(error.response?.data?.errorMessage || 'Something went wrong');
      }
    },

  });

  useEffect(() => {
    if (role) {
      const areaMap = {};
      (role.regionIds || []).forEach(entry => {
        areaMap[String(entry.regionId)] = (entry.areas || []).map(a => String(a));
      });

      formik.setValues({
        roleName: role.name || '',
        regionIds: (role.regionIds || []).map(r => String(r.regionId)),
        areaByRegion: areaMap,
        access: role.access || [],
        isLeadership: role.isLeadership || false,
        approver: role.approver || false,
        validator: role.validator || false,
        trackAttendance: role.trackAttendance || false,
      });

      setPermissions(expandPermissions(role.permissions || {}));
    }
  }, [role]);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await getRegions(projectId);
        const opts = response.regions.map(p => ({ value: String(p.id), label: p.name }));
        setRegionOptions(opts);
      } catch (error) {
        console.error('Failed to fetch regions:', error);
      }
    };
    fetchRegions();
  }, [projectId]);

  useEffect(() => {
    const selectedRegionIds = (formik.values.regionIds || []).map(String);

    const fetchAreas = async () => {
      for (const regionId of selectedRegionIds) {
        if (!areaOptions[regionId]) {
          try {
            const response = await getAreas(projectId, Number(regionId)); // ensure numeric for API
            const opts = response.areas.map(a => ({ value: String(a.id), label: a.name }));
            setAreaOptions(prev => ({ ...prev, [regionId]: opts }));
          } catch (error) {
            console.error(`Failed to fetch areas for region ${regionId}:`, error);
          }
        }
      }

      setAreaOptions(prev =>
        Object.fromEntries(
          Object.entries(prev).filter(([id]) => selectedRegionIds.includes(String(id)))
        )
      );

      formik.setFieldValue(
        'areaByRegion',
        Object.fromEntries(
          Object.entries(formik.values.areaByRegion || {}).filter(([id]) => selectedRegionIds.includes(String(id)))
        )
      );
    };

    if (selectedRegionIds.length > 0) {
      fetchAreas();
    }
  }, [formik.values.regionIds]);

  const transformPermissions = (permissions) =>
    Object.fromEntries(
      Object.entries(permissions).map(([section, actions]) => {
        const actionList = Object.keys(actions).filter(action => actions[action] === true);
        return [section, actionList];
      })
    );

  const handleCheckboxChange = (section, action) => {
    setPermissions(prev => {
      const sectionPerms = { ...prev[section] };
      if (action === 'Read') {
        const newState = !sectionPerms['Read'];
        return {
          ...prev,
          [section]: newState ? { Read: true } : {},
        };
      } else {
        const newPerms = {
          ...sectionPerms,
          [action]: !sectionPerms[action],
        };
        if (!sectionPerms['Read']) {
          newPerms['Read'] = true;
        }
        if (!newPerms[action]) {
          delete newPerms[action];
        }
        if (!newPerms['Read']) {
          return { ...prev, [section]: {} };
        }
        return {
          ...prev,
          [section]: newPerms,
        };
      }
    });
  };

  return (
    <Modal show={show} onHide={onClose} fullscreen="md-down" centered>
      <Modal.Header closeButton>
        <Modal.Title>{role ? 'Edit Role' : 'New Role'}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column" style={{ height: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Group className="mb-3" controlId="roleName">
            <Form.Label>Role Name</Form.Label>
            <Form.Control
              {...formik.getFieldProps('roleName')}
              isInvalid={formik.touched.roleName && !!formik.errors.roleName}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.roleName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="regionIds">
            <FormikSearchableDropdown
              label="Region Scope"
              name="regionIds"
              isMulti
              options={regionOptions}
              formik={formik}
            />
          </Form.Group>

          {formik.values.regionIds.map(regionId => (
            <Form.Group className="mb-3" key={regionId} controlId={`areaByRegion.${regionId}`}>
              <FormikSearchableDropdown
                label={`Areas for ${regionOptions.find(r => r.value === regionId)?.label || regionId}`}
                name={`areaByRegion.${regionId}`}
                isMulti
                options={areaOptions[regionId] || []}
                formik={formik}
                key={`area-${regionId}-${(areaOptions[regionId]?.length || 0)}`}
              />
            </Form.Group>
          ))}

          <Form.Group className="mb-3">
            <Form.Label>Access</Form.Label>
            <div className="d-flex gap-3 ms-2">
              {['web', 'mobile'].map((type) => (
                <Form.Check
                  key={type}
                  type="checkbox"
                  label={type.charAt(0).toUpperCase() + type.slice(1)}
                  checked={formik.values.access.includes(type)}
                  onChange={() => {
                    const currentAccess = formik.values.access;
                    const updated = currentAccess.includes(type)
                      ? currentAccess.filter((t) => t !== type)
                      : [...currentAccess, type];
                    formik.setFieldValue('access', updated);
                  }}
                  isInvalid={formik.touched.access && !!formik.errors.access}
                />
              ))}
            </div>
            <Form.Control.Feedback type="invalid" className="d-block">
              {formik.touched.access && formik.errors.access}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Track Attendance Checkbox */}
          {formik.values.access.includes('mobile') && (
            <Form.Group className="mb-3" controlId="trackAttendance">
              <Form.Check
                type="checkbox"
                label="Track Attendance"
                checked={formik.values.trackAttendance}
                onChange={(e) => formik.setFieldValue('trackAttendance', e.target.checked)}
              />
            </Form.Group>
          )}

          <Form.Group className="mb-3" controlId="isLeadership">
            <Form.Check
              type="checkbox"
              label="Leadership Role"
              checked={formik.values.isLeadership}
              onChange={(e) => formik.setFieldValue('isLeadership', e.target.checked)}
            />
          </Form.Group>
          
          {/* Approver Checkbox */}
          {currentProject?.settings?.approval === true && formik.values.isLeadership === true && formik.values.access.includes('mobile') && (
            <Form.Group className="mb-3" controlId="approverCheckbox">
              <Form.Check
                type="checkbox"
                label="Approver"
                {...formik.getFieldProps('approver')}
                checked={formik.values.approver}
              />
            </Form.Group>
          )}

          {/* Validator Checkbox */}
          {currentProject?.settings?.validation === true && formik.values.isLeadership === true && formik.values.access.includes('web') && (
            <Form.Group className="mb-3" controlId="validatorCheckbox">
              <Form.Check
                type="checkbox"
                label="Validator"
                {...formik.getFieldProps('validator')}
                checked={formik.values.validator}
              />
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Permissions</Form.Label>
            <div className="ms-3">
              {['Stores', 'Users', 'Engagements (Reports)'].map(
                (section, idx) => (
                  <div key={idx} className="mb-2">
                    <div>{section}</div>
                    <div className="d-flex flex-wrap gap-2">
                      {['Create', 'Read', 'Update', 'Delete'].map(action => (
                        <Form.Check
                          key={action}
                          type="checkbox"
                          label={action}
                          className="text-capitalize"
                          checked={permissions[section]?.[action] || false}
                          disabled={
                            (section === 'Users' && action === 'Read') ||
                            (section === 'Stores' && action === 'Read') ||
                            (section === 'Engagements (Reports)' && action === 'Create') ||
                            (section === 'Engagements (Reports)' && action === 'Read') ||
                            (section === 'Engagements (Reports)' && action === 'Update')
                          }
                          onChange={() => handleCheckboxChange(section, action)}
                        />
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </Form.Group>

          <div className="d-flex justify-content-center mt-3">
            <Button type="submit" variant="primary" className="px-5">
              Submit
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditRoleModal;
